import React from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Castle, 
  Ship, 
  Mountain, 
  ChevronDown 
} from 'lucide-react';
import { useUser } from '../../../context/UserContext';
import { canAccessAdmin } from '../../../utils/roleUtils';

const DestinationFilter = ({ selectedDestination, onDestinationChange }) => {
  const { user } = useUser();
  
  // Check if user has access to new destination features
  const hasNewDestinationAccess = () => {
    return user && canAccessAdmin(user.role);
  };
  
  const allDestinations = [
    {
      id: 'all',
      name: 'All Destinations',
      icon: MapPin,
      color: 'gray',
      description: 'View all trips across all destinations'
    },
    {
      id: 'disney_world',
      name: 'Walt Disney World',
      icon: Castle,
      color: 'blue',
      description: 'Magic Kingdom, EPCOT, Hollywood Studios, Animal Kingdom'
    },
    {
      id: 'disneyland',
      name: 'Disneyland Resort',
      icon: Castle,
      color: 'purple',
      description: 'Disneyland Park, California Adventure'
    },
    {
      id: 'universal_orlando',
      name: 'Universal Orlando',
      icon: Mountain,
      color: 'green',
      description: 'Universal Studios, Islands of Adventure, Volcano Bay'
    },
    {
      id: 'disney_cruise',
      name: 'Disney Cruise Line',
      icon: Ship,
      color: 'teal',
      description: 'Disney Magic, Wonder, Dream, Fantasy, Wish'
    }
  ];
  
  // Filter destinations based on user access
  const destinations = hasNewDestinationAccess() 
    ? allDestinations 
    : allDestinations.filter(dest => ['all', 'disney_world'].includes(dest.id));

  const getDestinationById = (id) => destinations.find(dest => dest.id === id) || destinations[0];

  const selectedDest = getDestinationById(selectedDestination);
  const IconComponent = selectedDest.icon;

  return (
    <div className="relative">
      {/* Destination description */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-1 text-xs text-gray-500"
      >
        {selectedDest.description}
      </motion.div>
      
      <div className="relative">
        <select
          value={selectedDestination}
          onChange={(e) => onDestinationChange(e.target.value)}
          className="appearance-none pl-10 pr-10 py-2 w-full text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors"
        >
          {destinations.map((destination) => (
            <option key={destination.id} value={destination.id}>
              {destination.name}
            </option>
          ))}
        </select>
        
        {/* Custom dropdown icon */}
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <IconComponent className={`w-4 h-4 text-${selectedDest.color}-600`} />
        </div>
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </div>
      </div>
    </div>
  );
};

export default DestinationFilter;
